import { Component, Input, ViewChild, ChangeDetectionStrategy } from "@angular/core";

import { AdminService, BaseComponent, ContentService, Course, EventBusService, LearnContentType, LearnGroup, Lesson, ProductService, UserService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { ItemSpec } from "@nativescript/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { interval, Subject } from "rxjs";
import { skip, take, takeUntil, throttle } from "rxjs/operators";
import { UploadFileComponent } from "../upload-file/upload-file.component";
import { LearnContentPickerComponent } from "../../learn-content-picker/learn-content-picker.component";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-add-edit-learn-group",
  templateUrl: "add-edit-learn-group.component.html",
  standalone: false,
})
export class AddEditLearnGroupComponent extends BaseComponent {
  // [animation] on spinner icons also binds ngbTooltip's boolean input on the same element.
  public readonly spinAnimation: any = "spin";
  @ViewChild(LearnContentPickerComponent) learnPicker: LearnContentPickerComponent;
  @Input() learnGroup: LearnGroup = {
    title: "",
    description: "",
    snippet: "",
    level: -1,
    status: "pending",
  };

  @Input() type: string;
  @Input() video: string;

  public selectedSection: string = "associate";

  public learnGroupLevel: number;
  public pickerType: LearnContentType = "video";
  public pickerHint = "";
  public pickerPlaceholder = "";
  public pickerExcludeIds: any[] = [];
  public flagshipProductResults: Array<any>;
  private _flagshipProductQuery: string;
  private _flagshipProductQueryStream$: Subject<any> = new Subject();
  set flagshipProductQuery(query) {
    this._flagshipProductQuery = query;
    if (this._flagshipProductQuery) {
      this._flagshipProductQueryStream$.next(query);
    } else {
      this.flagshipProductResults = undefined;
    }
  }

  get flagshipProductQuery() {
    return this._flagshipProductQuery;
  }
  public searchingFlagshipProducts = false;

  constructor(
    userService: UserService,
    public activeModal: NgbActiveModal,
    public contentService: ContentService,
    public appService: AppService,
    public eventbusService: EventBusService,
    public productService: ProductService,
    public adminService: AdminService
  ) {
    super(userService);
  }

  ngOnInit() {
    if (!this.learnGroup.id) this.learnGroup.type = this.type;
    if (this.learnGroup.id && !this.learnGroup.level) this.learnGroup.level == -1;
    if (this.type) this.learnGroup.type = this.type;
    if (this.video) this.addVideo(this.video);
    if (this.learnGroup.type == "folder") this.learnGroup.status = "approved";
    this._flagshipProductQueryStream$
      .pipe(
        skip(1),
        throttle(
          () => {
            this.searchingFlagshipProducts = true;
            return interval(1000);
          },
          { leading: true, trailing: true }
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.productAutocomplete();
      });
    this.updatePicker();
  }

  productAutocomplete() {
    if (this.flagshipProductQuery) {
      this.productService.searchv2("title", this.flagshipProductQuery, 5).subscribe((result: Array<any>) => {
        this.flagshipProductResults = result;
        this.searchingFlagshipProducts = false;
      });
    } else {
      this.flagshipProductResults = [];
      this.searchingFlagshipProducts = false;
    }
  }

  public updatingFlagshipMap: any = {};
  setAsFlagship(product) {
    this.updatingFlagshipMap[product.id] = true;
    let args: any = {
      productId: product.id,
    };
    args[this.learnGroup.type == "course" ? "courseId" : "lessonId"] = this.learnGroup.id;
    this.adminService.updateProductFlagship(args).subscribe((result) => {
      this.updatingFlagshipMap[product.id] = false;
      this.flagshipProductQuery = "";
      this.flagshipProductResults = [];
      if (!this.learnGroup.flagship_products) this.learnGroup.flagship_products = [];
      this.learnGroup.flagship_products.push(product);
    });
  }

  unsetAsFlagship(product) {
    let args: any = {
      productId: product.id,
    };
    args[this.learnGroup.type == "course" ? "courseId" : "lessonId"] = null;
    this.updatingFlagshipMap[product.id] = true;
    this.adminService.updateProductFlagship(args).subscribe((result) => {
      this.updatingFlagshipMap[product.id] = false;
      this.learnGroup.flagship_products = this.learnGroup.flagship_products.filter((item) => item.id != product.id);
    });
  }

  updatePicker() {
    if (!this.learnGroup) return;
    const isCourse = this.learnGroup.type == "course";
    const noun = isCourse ? "lesson" : "video";
    this.pickerType = isCourse ? "lesson" : "video";
    this.pickerHint = `Start typing to find a ${noun} to add to this ${this.learnGroup.type}...`;
    this.pickerPlaceholder = `Enter a ${noun} title or ID...`;
    this.pickerExcludeIds = isCourse ? this.learnGroup.lesson_ids || [] : this.learnGroup.video_ids || [];
  }

  onPickerAdded(item) {
    if (this.learnGroup.type == "course") this.addLesson(item);
    else this.addVideo(item);
  }

  addVideo(video) {
    if (this.learnGroup.type == "lesson" && this.learnGroup.status == "approved" && video.status != "approved") {
      alert("You cannot add an unapproved video to an approved lesson. Click the yellow PENDING button to approve this video before adding to the lesson.");
    } else {
      if (!this.learnGroup.videos) this.learnGroup.videos = [];
      if (!this.learnGroup.video_ids) this.learnGroup.video_ids = [];
      this.learnGroup.videos.push(video);
      this.learnGroup.video_ids.push(video.id);
      this.learnPicker?.removeItem(video.id);
      this.updatePicker();
    }
  }

  sortVideos() {
    this.learnGroup.videos.sort((a: any, b: any) => {
      return new Date(a.created) > new Date(b.created) ? -1 : 1;
    });
  }

  removeVideo(video) {
    this.learnGroup.videos = this.learnGroup.videos.filter((item) => item.id != video.id);
    this.learnGroup.video_ids = this.learnGroup.video_ids.filter((id) => id != video.id);
    this.updatePicker();
  }

  addLesson(lesson) {
    if (this.learnGroup.type == "course" && this.learnGroup.status == "approved" && lesson.status != "approved") {
      alert("You cannot add an unapproved lesson to an approved course. Click the yellow PENDING button to approve this lesson before adding to the course.");
    } else {
      if (!this.learnGroup.lessons) this.learnGroup.lessons = [];
      if (!this.learnGroup.lesson_ids) this.learnGroup.lesson_ids = [];
      this.learnGroup.lessons.push(lesson);
      this.learnGroup.lesson_ids.push(lesson.id);
      this.learnPicker?.clear();
      this.updatePicker();
    }
  }

  removeLesson(lesson) {
    this.learnGroup.lessons = this.learnGroup.lessons.filter((item) => item.id != lesson.id);
    this.learnGroup.lesson_ids = this.learnGroup.lesson_ids.filter((id) => id != lesson.id);
    this.updatePicker();
  }

  addImage() {
    const modalRef = this.appService.showModal(UploadFileComponent, { size: "lg", ariaLabelledBy: "modal-title" });
    modalRef.componentInstance.uploadMethod = "add_learn_group_image";
    modalRef.componentInstance.cropRatio = "1.77777777777";
    modalRef.componentInstance.fileUploaded.pipe(take(1)).subscribe((result) => {
      if (result.url) this.learnGroup.image = result.url;
    });
  }

  addResources() {
    const modalRef = this.appService.showModal(UploadFileComponent, { size: "lg", ariaLabelledBy: "modal-title" });
    modalRef.componentInstance.uploadMethod = "add_learn_group_resources";
    modalRef.componentInstance.allowedTypes = ["application/octet-stream", "application/x-compress", "application/x-compressed", "application/x-zip-compressed", "application/zip", "multipart/x-zip"];
    modalRef.componentInstance.allowedFormatString = ".zip";
    modalRef.componentInstance.fileUploaded.pipe(take(1)).subscribe((result) => {
      if (result.url) this.learnGroup.resources_link = result.url;
    });
  }

  validate(args) {
    if (!args.title) {
      alert("Please fill out all the required fields.");
      return false;
    } else if (args.type != "folder" && (!args.description || !args.snippet)) {
      alert("Please fill out all the required fields.");
      return false;
    } else if (args.type == "lesson" && args.status == "approved" && !args.video_ids.length) {
      alert("You cannot approve a lesson that has no videos.");
      return false;
    } else if (args.type == "course" && args.status == "approved" && !args.lesson_ids.length) {
      alert("You cannot approve a course that has no lessons.");
      return false;
    }
    return true;
  }

  delete() {
    let message = "Are you sure you  want to delete this " + this.learnGroup.type + "? ";
    if (this.learnGroup.type != "folder") message += "Any users who have enrolled in this " + this.learnGroup.type + " will still have access until they complete it.";
    if (confirm(message)) {
      this.contentService.deleteLearnGroup(this.learnGroup.id).subscribe((result) => {
        this.activeModal.close(true);
      });
    }
  }

  save() {
    let args: any = {
      title: this.learnGroup.title,
      description: this.learnGroup.description,
      snippet: this.learnGroup.snippet,
      type: this.learnGroup.type,
      status: this.learnGroup.status,
      level: this.learnGroup.level == -1 ? null : this.learnGroup.level,
      subscription_only: this.learnGroup.subscription_only,
    };

    if (this.learnGroup.image) args.image = this.learnGroup.image;
    if (this.learnGroup.id) args.id = this.learnGroup.id;
    if (this.learnGroup.resources_link) args.resources_link = this.learnGroup.resources_link;
    if (this.learnGroup.type == "lesson" || this.learnGroup.type == "folder") {
      if (this.learnGroup.video_ids) args.video_ids = [];
      if (this.learnGroup.videos && this.learnGroup.videos.length) {
        this.learnGroup.videos.forEach((item) => {
          args.video_ids.push(item.id);
        });
      }
    } else if (this.learnGroup.type == "course") {
      if (this.learnGroup.lesson_ids) args.lesson_ids = [];
      if (this.learnGroup.lessons && this.learnGroup.lessons.length) {
        this.learnGroup.lessons.forEach((item) => {
          args.lesson_ids.push(item.id);
        });
      }
    }
    if (args.level == -1) args.level = null;
    if (this.validate(args)) {
      this.contentService.addOrUpdateLearnGroup(args).subscribe((result) => {
        this.activeModal.close(true);
      });
    }
  }
}
